import { useEffect, useState } from "react";
import { IEditMatchDTO, IMatchDetailDTO } from "../../models/IMatch";
import moment from "moment";
import { Alert, Button, DatePicker, Dialog, Field, FormGrid, Input, Select } from "../../ui";
import "./styles.scss";
import { updateAmericanoMatch, updateKnockoutMatch, updateMatch, updateMexicanoMatch } from "../../api/tournaments";
import { isLiveRoundFormat } from "../../utils/methods";

type MatchEditDialogProps = {
  match: IMatchDetailDTO;
  isOpen: boolean;
  mode: "time" | "venue" | "score" | "best" | "all" | "schedule";
  onClose: (udpate?: boolean) => void;
};

const MatchEditDialog = (props: MatchEditDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [venue, setVenue] = useState("");
  const [bestOf, setBestOf] = useState<number>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
  };

  const handleTimeChange = (time: Date | null) => {
    setSelectedTime(time);
  };

  const handleClose = (update?: boolean) => {
    props.onClose(update ?? undefined);
  };

  const getModeTitle = () => {
    return props.mode === "time"
      ? "Change date & time"
      : props.mode === "schedule"
      ? "Change schedule"
      : props.mode === "score"
      ? "Edit game scores"
      : props.mode === "all"
      ? "Edit match"
      : props.mode == "best"
      ? "Change Best Of"
      : `Change ${props.mode}`;
  };

  const handleConfirm = async () => {
    setLoading(true);

    let requestBody: IEditMatchDTO = {
      gameScores: props.match.gameScores.map((gs) => ({
        gameNumber: gs.gameNumber,
        homeScore: gs.homeScore,
        awayScore: gs.awayScore,
      })),
    };

    const startTime =
      selectedDate && selectedTime
        ? moment(selectedDate)
            .set({
              hour: selectedTime.getHours(),
              minute: selectedTime.getMinutes(),
              second: 0,
              millisecond: 0,
            })
            .utc()
            .toISOString()
        : undefined;

    if (props.mode === "all" || props.mode === "time" || props.mode === "schedule") {
      requestBody.startTime = startTime;
    }

    if (props.mode === "all" || props.mode === "best") {
      requestBody.bestOf = bestOf;
    }

    if (props.mode === "all" || props.mode === "venue" || props.mode === "schedule") {
      requestBody.venue = venue;
    }

    try {
      setError(null);
      if (props.match.format === "Knockout") await updateKnockoutMatch(props.match.id, requestBody);
      else if (props.match.format === "Americano") await updateAmericanoMatch(props.match.id, requestBody);
      // Mexicano and Roulette share the api/Mexicano routes.
      else if (isLiveRoundFormat(props.match.format)) await updateMexicanoMatch(props.match.id, requestBody);
      else await updateMatch(props.match.id, requestBody);
      handleClose(true);
    } catch (error: any) {
      console.error("error editing match", error.message);
      setError(error?.message || "Failed to update match. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (props.match?.startTime) {
      setSelectedDate(new Date(props.match.startTime));
      setSelectedTime(new Date(props.match.startTime));
    }

    if (props.match?.venue) setVenue(props.match.venue);
    if (props.match?.bestOf) setBestOf(props.match.bestOf);
  }, [props.match?.startTime, props.match?.venue, props.match?.bestOf]);

  useEffect(() => {
    if (props.isOpen) {
      setError(null);
    }
  }, [props.isOpen]);

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      placement="side"
      size="sm"
      title={getModeTitle()}
      busy={loading}
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={loading}>
            Confirm
          </Button>
        </>
      }
    >
      <div className="match-edit-dialog">
        {(props.mode === "time" || props.mode === "all" || props.mode === "schedule") && (
          <FormGrid columns={2}>
            <Field label="Date">
              {(id) => (
                <DatePicker
                  id={id}
                  minDate={new Date(moment(props.match?.startDate).format("YYYY-MM-DD"))}
                  maxDate={new Date(moment(props.match?.endDate).format("YYYY-MM-DD"))}
                  selected={selectedDate}
                  onChange={handleDateChange}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Select date"
                />
              )}
            </Field>
            <Field label="Time">
              {(id) => (
                <DatePicker
                  id={id}
                  selected={selectedTime}
                  onChange={handleTimeChange}
                  mode="time"
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="hh:mm aa"
                  placeholderText="Select time"
                />
              )}
            </Field>
          </FormGrid>
        )}
        {(props.mode === "venue" || props.mode === "all" || props.mode === "schedule") && (
          <Field label="Venue">
            {(id) => <Input id={id} placeholder="Enter venue" value={venue} onChange={(e) => setVenue(e.target.value)} />}
          </Field>
        )}
        {(props.mode === "best" || props.mode === "all") && (
          <Field label="Best Of">
            {(id) => (
              <Select id={id} block value={bestOf ?? ""} onChange={(e) => setBestOf(Number(e.target.value))}>
                <option value={1}>1</option>
                <option value={3}>3</option>
                <option value={5}>5</option>
              </Select>
            )}
          </Field>
        )}
        {(props.mode === "score" || props.mode === "all") && <></>}
        {error && <Alert tone="danger">{error}</Alert>}
      </div>
    </Dialog>
  );
};

export default MatchEditDialog;
